<script>
import LandLeaseService from "@/services/LandLeaseService";
import ProjectService from "@/services/ProjectService";
import UserService from "@/services/UserService";
import VFilters from "@/components/Search/VFilters";
import VLeaseTable from "./components/VLeaseTable.vue";
import VLeaseViewer from "./components/VLeaseViewer.vue";
import VLandLeaseForm from "@/components/Forms/VLandLeaseForm";
import { LeaseType, LeaseStatus } from "@/constants/LandLease.const";

export default {
    name: "VIndex",
    components: { VFilters, VLeaseTable, VLeaseViewer, VLandLeaseForm },
    data() {
        return {
            loading: true,
            leases: [],
            meta: {},
            projects: [],
            users: [],
            showModal: false,
            leaseContext: null,
            selectedLeaseId: null
        };
    },
    computed: {
        stats() {
            return this.meta.stats || {};
        },
        optTypes() {
            return { "": this.$t("land_leases.all_types"), ...LeaseType };
        },
        optStatuses() {
            return { "": this.$t("land_leases.all_statuses"), ...LeaseStatus };
        }
    },
    async created() {
        if (this.$route.query.lease) {
            this.selectedLeaseId = parseInt(this.$route.query.lease);
        }
        await Promise.all([this.fetchProjects(), this.fetchUsers()]);
        this.initSearch();
        await this.fetchData();
        this.loading = false;
        this.$apiEnableWatcher();
        if (this.selectedLeaseId) {
            this.$nextTick(() => this.scrollToViewer());
        }
    },
    methods: {
        initSearch() {
            this.apiSearchFilter = {
                search: "",
                lease_type: "",
                project_id: "",
                user_id: "",
                status: ""
            };
            this.apiPaginator = {
                sort: "LandLeases.id",
                direction: "desc"
            };
        },
        resetSearch() {
            this.$resetSearch();
            this.initSearch();
        },
        async fetchData() {
            try {
                this.loading = true;
                let params = this.$buildParams();
                const { data } = await LandLeaseService.GET(params);
                this.leases = data.data;
                this.meta = data.meta;
            } catch (e) {
                console.log("fetchData e", e);
            }
            this.loading = false;
        },
        async fetchProjects() {
            try {
                const r = await ProjectService.GET_dropdown({
                    invoice_setup: "projects",
                    only_projects: 1,
                    status: [1, 5]
                });
                this.projects = r.data.data;
                this.projects.unshift({
                    value: "",
                    label: this.$t("land_leases.all_projects")
                });
            } catch (e) {
                console.log("fetchProjects e", e);
            }
        },
        async fetchUsers() {
            try {
                const r = await UserService.dropdown({ preset: "pm" });
                this.users = r.data.data;
                this.users.unshift({
                    value: "",
                    label: this.$t("land_leases.all_pms")
                });
            } catch (e) {
                console.log("fetchUsers e", e);
            }
        },
        setLeaseContext(ctx) {
            this.leaseContext = ctx;
            this.showModal = true;
        },
        viewLease(lease) {
            this.selectedLeaseId = lease.id;
            this.$router.replace({
                query: { ...this.$route.query, lease: lease.id }
            });
            this.$nextTick(() => this.scrollToViewer());
        },
        closeViewer() {
            this.selectedLeaseId = null;
            const { lease, ...rest } = this.$route.query;
            this.$router.replace({ query: rest });
        },
        scrollToViewer() {
            const el = this.$refs.leaseViewer;
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        },
        async closeReload() {
            await this.fetchData();
            this.showModal = false;
            if (this.selectedLeaseId) {
                const still = this.leases.find(
                    l => l.id === this.selectedLeaseId
                );
                if (still) {
                    this.$refs.viewer.fetchLease();
                } else {
                    this.closeViewer();
                }
            }
        }
    }
};
</script>

<template>
    <div class="v-land-leases">
        <base-header class="pb-1" type="">
            <bread-bag>
                <base-button
                    size="sm"
                    type="primary"
                    @click="setLeaseContext({ empty: true })"
                >
                    <i class="far fa-plus"></i>
                    {{ $t("land_leases.add_contract") }}
                </base-button>
            </bread-bag>

            <div class="row pt-5 stats">
                <div class="col-12 col-md-6 col-lg-3">
                    <nav-stats-card
                        :title="$t('land_leases.stats_total')"
                        :value="stats.total || 0"
                        type="default"
                        icon="far fa-file-contract"
                    />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                    <nav-stats-card
                        :title="$t('land_leases.stats_not_signed')"
                        :value="stats.not_signed || 0"
                        :type="stats.not_signed ? 'warning' : 'default'"
                        icon="far fa-file-signature"
                    />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                    <nav-stats-card
                        :title="$t('land_leases.stats_close_to_deadline')"
                        :value="stats.close_to_deadline || 0"
                        :type="stats.close_to_deadline ? 'warning' : 'default'"
                        icon="far fa-bell-exclamation"
                    />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                    <nav-stats-card
                        :title="$t('land_leases.stats_exceeded_deadlines')"
                        :value="stats.exceeded_deadlines || 0"
                        :type="stats.exceeded_deadlines ? 'danger' : 'default'"
                        icon="far fa-alarm-exclamation"
                    />
                </div>
            </div>
        </base-header>
        <div class="container-fluid">
            <div class="row card-wrapper">
                <div class="col-12">
                    <v-filters
                        :enable-filters="true"
                        v-model="apiSearchFilter.search"
                        :search="true"
                        start-open
                        :search-placeholder="
                            $t('land_leases.placeholder_search')
                        "
                        @reset-search="resetSearch"
                    >
                        <template slot="default">
                            <div class="col">
                                <FormulateInput
                                    v-model="apiSearchFilter.lease_type"
                                    :placeholder="$t('land_leases.all_types')"
                                    type="multiselect"
                                    :multiple="false"
                                    :options="optTypes"
                                />
                            </div>
                            <div class="col">
                                <FormulateInput
                                    v-model="apiSearchFilter.project_id"
                                    :placeholder="
                                        $t('land_leases.all_projects')
                                    "
                                    type="multiselect"
                                    :multiple="false"
                                    :options="projects"
                                />
                            </div>
                            <div class="col">
                                <FormulateInput
                                    v-model="apiSearchFilter.user_id"
                                    :placeholder="$t('land_leases.all_pms')"
                                    type="multiselect"
                                    :multiple="false"
                                    :options="users"
                                />
                            </div>
                            <div class="col">
                                <FormulateInput
                                    v-model="apiSearchFilter.status"
                                    :placeholder="
                                        $t('land_leases.all_statuses')
                                    "
                                    type="multiselect"
                                    :multiple="false"
                                    :options="optStatuses"
                                />
                            </div>
                        </template>
                    </v-filters>
                </div>
                <div class="col-12">
                    <card body-classes="px-0">
                        <v-loader :loading="loading" v-if="loading" />
                        <template v-else-if="leases.length">
                            <v-lease-table
                                :leases="leases"
                                :paginator="apiPaginator"
                                :meta="meta"
                                @sort="$handleSorting($event)"
                                @paginate="$handlePagination($event)"
                                @edit="setLeaseContext($event)"
                                @view="viewLease($event)"
                            />
                        </template>
                        <div class="px-mg" v-else>
                            <div class="alert alert-warning">
                                {{ $t("table_no_data_found") }}
                            </div>
                        </div>
                    </card>
                </div>

                <div
                    class="col-12"
                    ref="leaseViewer"
                    id="land-lease-viewer"
                    v-if="selectedLeaseId"
                >
                    <v-lease-viewer
                        ref="viewer"
                        :lease-id="selectedLeaseId"
                        @close="closeViewer"
                        @edit="setLeaseContext($event)"
                    />
                </div>
            </div>
        </div>

        <portal to="modals">
            <v-land-lease-form
                v-if="showModal"
                :context="leaseContext"
                @close="showModal = false"
                @close-reload="closeReload"
            />
        </portal>
    </div>
</template>
